import { useEffect, useRef, useState } from "react";
import type {
  FunnelDTO,
  PromptBuilderInput,
  PromptBuilderMode,
  PromptBuilderResult,
} from "@ai-crm/shared";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { PaidFeatureGate, type PaidFeatureAccess } from "./paid-feature-gate";

export interface PromptBuilderState {
  mode: PromptBuilderMode | null;
  answers: PromptBuilderInput;
}

export function createEmptyPromptBuilderAnswers(): PromptBuilderInput {
  return {
    agentGoal: "",
    companyContext: "",
    customerInformation: "",
    conversationFlow: "",
    permissionsAndRestrictions: "",
    handoffConditions: "",
    communicationStyle: "",
    additionalComments: "",
  };
}

export function createEmptyPromptBuilderState(): PromptBuilderState {
  return { mode: null, answers: createEmptyPromptBuilderAnswers() };
}

export interface PromptBuilderProps {
  generatePrompt: (input: PromptBuilderInput) => Promise<PromptBuilderResult>;
  value: string;
  onChange(value: string): void;
  state: PromptBuilderState;
  onStateChange(state: PromptBuilderState): void;
  agents?: readonly FunnelDTO[];
  loadAgent?: (
    id: string,
  ) => Promise<Pick<FunnelDTO, "systemPrompt" | "escalationPrompt">>;
  onCopyBlock?: (block: string | null) => void;
  copyContextSignature?: string;
  disabled?: boolean;
  manualLabel?: string;
  escalationAccess?: PaidFeatureAccess;
}

export const QUESTIONS: ReadonlyArray<{
  key: keyof PromptBuilderInput;
  title: string;
  example: string;
  optional?: boolean;
}> = [
  {
    key: "agentGoal",
    title: "Что должен делать агент?",
    example:
      "Например: консультировать, квалифицировать лид или записывать на встречу.",
  },
  {
    key: "companyContext",
    title: "Расскажите о компании, товарах или услугах.",
    example:
      "Укажите только факты, которыми агент может пользоваться в ответах.",
  },
  {
    key: "customerInformation",
    title: "Что агент должен узнать у клиента?",
    example: "Например: имя, город, задача, бюджет и сроки.",
  },
  {
    key: "conversationFlow",
    title: "Как должен проходить разговор?",
    example: "Опишите порядок вопросов и ожидаемый следующий шаг.",
  },
  {
    key: "permissionsAndRestrictions",
    title: "Что агенту разрешено и запрещено?",
    example: "Например: можно называть цены, нельзя обещать скидки.",
  },
  {
    key: "handoffConditions",
    title: "Когда нужно передать разговор менеджеру?",
    example: "Например: клиент просит человека, жалуется или готов купить.",
  },
  {
    key: "communicationStyle",
    title: "Как агент должен общаться?",
    example: "Укажите тон, обращение на ты или вы, длину ответов и язык.",
  },
  {
    key: "additionalComments",
    title: "Дополнительные комментарии",
    example: "Всё важное, что не подошло к предыдущим вопросам.",
    optional: true,
  },
];

export function PromptBuilder({
  value,
  onChange,
  state,
  onStateChange,
  agents = [],
  loadAgent,
  generatePrompt,
  onCopyBlock,
  copyContextSignature = "",
  disabled = false,
  manualLabel = "Системный промпт",
  escalationAccess,
}: PromptBuilderProps) {
  const [questionIndex, setQuestionIndex] = useState(0);
  const [result, setResult] = useState<PromptBuilderResult | null>(null);
  const [resultPrompt, setResultPrompt] = useState("");
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [sourceAgentId, setSourceAgentId] = useState("");
  const copySignature = JSON.stringify([
    value,
    state,
    sourceAgentId,
    disabled,
    copyContextSignature,
    escalationAccess?.allowed,
  ]);
  const copyVersion = useRef({ signature: copySignature, revision: 0 });
  if (copyVersion.current.signature !== copySignature) {
    copyVersion.current = {
      signature: copySignature,
      revision: copyVersion.current.revision + 1,
    };
  }
  const copySequence = useRef(0);
  const copyCallbacks = useRef({ applyPrompt, onCopyBlock, escalationAllowed: escalationAccess?.allowed !== false });
  copyCallbacks.current = { applyPrompt, onCopyBlock, escalationAllowed: escalationAccess?.allowed !== false };
  useEffect(
    () => () => {
      copySequence.current += 1;
    },
    [],
  );
  const questionRef = useRef<HTMLTextAreaElement | null>(null);
  const generationInFlightRef = useRef(false);
  const totalAnswerLength = Object.values(state.answers).reduce(
    (total, answer) => total + (answer?.length ?? 0),
    0,
  );
  const questionnaireTooLarge = totalAnswerLength > 16_000;

  useEffect(() => {
    if (state.mode === "assistant" && result === null) {
      questionRef.current?.focus();
    }
  }, [questionIndex, result, state.mode]);

  function chooseMode(mode: PromptBuilderMode | null) {
    if (disabled) return;
    setResult(null);
    setGenerationError(null);
    setQuestionIndex(0);
    onStateChange({ ...state, mode });
  }

  function updateAnswer(key: keyof PromptBuilderInput, answer: string) {
    onStateChange({
      ...state,
      answers: { ...state.answers, [key]: answer },
    });
  }

  async function generate() {
    if (
      disabled ||
      generating ||
      generationInFlightRef.current ||
      questionnaireTooLarge
    ) {
      return;
    }
    generationInFlightRef.current = true;
    setGenerating(true);
    setGenerationError(null);
    try {
      const generated = await generatePrompt(state.answers);
      setResult(generated);
      setResultPrompt(generated.prompt);
    } catch (error) {
      setGenerationError(
        error instanceof Error ? error.message : "Не удалось составить промпт",
      );
    } finally {
      generationInFlightRef.current = false;
      setGenerating(false);
    }
  }

  function applyPrompt(prompt: string) {
    if (disabled) return;
    onChange(prompt);
    setResult(null);
    setGenerationError(null);
    onStateChange({ ...state, mode: "manual" });
  }

  if (state.mode === null) {
    return (
      <div className="grid gap-2 sm:grid-cols-2">
        <Button
          type="button"
          onClick={() => chooseMode("assistant")}
          disabled={disabled}
        >
          Создать с помощником
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => chooseMode("manual")}
          disabled={disabled}
        >
          Написать самостоятельно
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => chooseMode("copy")}
          disabled={disabled || agents.length === 0}
        >
          Скопировать из другого агента
        </Button>
      </div>
    );
  }

  if (state.mode === "assistant" && result !== null) {
    return (
      <div className="space-y-4">
        <div className="space-y-1">
          <Label htmlFor="generated-system-prompt">Готовый промпт</Label>
          <Textarea
            id="generated-system-prompt"
            rows={12}
            maxLength={10_000}
            value={resultPrompt}
            onChange={(event) => setResultPrompt(event.target.value)}
            disabled={disabled}
          />
        </div>
        {result.warnings.length > 0 && (
          <div className="rounded border border-warning-border bg-warning-surface text-warning-foreground p-3 text-sm">
            <div className="font-medium">Предупреждения</div>
            <ul className="mt-1 list-disc pl-5">
              {result.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        )}
        {result.missingInformation.length > 0 && (
          <div className="rounded border p-3 text-sm">
            <div className="font-medium">Что стоит уточнить</div>
            <ul className="mt-1 list-disc pl-5">
              {result.missingInformation.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        )}
        {generationError && (
          <div
            role="alert"
            className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
          >
            {generationError}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={() => applyPrompt(resultPrompt)}
            disabled={disabled || resultPrompt.trim().length === 0}
          >
            Использовать промпт
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setResult(null)}
            disabled={disabled}
          >
            Вернуться к ответам
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={generate}
            disabled={disabled || generating}
          >
            {generating ? "Составляем..." : "Составить заново"}
          </Button>
        </div>
      </div>
    );
  }

  if (state.mode === "assistant") {
    const question = QUESTIONS[questionIndex]!;
    const isLast = questionIndex === QUESTIONS.length - 1;
    return (
      <div className="space-y-4">
        <div className="text-sm text-muted-foreground">
          Вопрос {questionIndex + 1} из {QUESTIONS.length}
        </div>
        <PaidFeatureGate allowed={question.key !== "handoffConditions" || escalationAccess?.allowed !== false} title="Условия передачи менеджеру" preview={<div className="min-h-52 rounded border bg-muted p-4">Условия передачи менеджеру</div>} upgradeAction={escalationAccess?.upgradeAction}>
        <div className="space-y-1">
          <Label htmlFor={`prompt-answer-${question.key}`}>
            {question.title}
            {question.optional ? " (необязательно)" : ""}
          </Label>
          <p className="text-xs text-muted-foreground">{question.example}</p>
          <Textarea
            ref={questionRef}
            id={`prompt-answer-${question.key}`}
            rows={6}
            maxLength={4_000}
            value={state.answers[question.key] ?? ""}
            onChange={(event) => updateAnswer(question.key, event.target.value)}
            disabled={disabled || generating}
          />
        </div>
        </PaidFeatureGate>
        {generationError && (
          <div
            role="alert"
            className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
          >
            {generationError}
          </div>
        )}
        {questionnaireTooLarge && (
          <div
            role="alert"
            className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
          >
            Анкета не может быть длиннее 16000 символов. Сократите ответы перед
            генерацией.
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={
              isLast ? generate : () => setQuestionIndex((index) => index + 1)
            }
            disabled={
              disabled || generating || (isLast && questionnaireTooLarge)
            }
          >
            {isLast
              ? generating
                ? "Составляем..."
                : "Составить промпт"
              : "Далее"}
          </Button>
          {questionIndex > 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setQuestionIndex((index) => index - 1)}
              disabled={disabled || generating}
            >
              Назад
            </Button>
          )}
          {generationError && (
            <Button
              type="button"
              variant="outline"
              onClick={generate}
              disabled={disabled || generating}
            >
              Попробовать снова
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            onClick={() => chooseMode(null)}
            disabled={disabled || generating}
          >
            Выбрать другой способ
          </Button>
        </div>
      </div>
    );
  }

  if (state.mode === "manual") {
    return (
      <div className="space-y-3">
        <Label htmlFor="manual-system-prompt">{manualLabel}</Label>
        <Textarea
          id="manual-system-prompt"
          rows={10}
          maxLength={10_000}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
        />
        <Button
          type="button"
          variant="ghost"
          onClick={() => chooseMode(null)}
          disabled={disabled}
        >
          Выбрать другой способ
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {generationError && <p role="alert">{generationError}</p>}
      <Label htmlFor="source-agent">Агент-источник</Label>
      <Select
        id="source-agent"
        value={sourceAgentId}
        onChange={(event) => setSourceAgentId(event.target.value)}
        disabled={disabled}
      >
        <option value="">— выберите агента —</option>
        {agents.map((agent) => (
          <option key={agent.id} value={agent.id}>
            {agent.name}
          </option>
        ))}
      </Select>
      <div className="flex gap-2">
        <Button
          type="button"
          onClick={async () => {
            if (!loadAgent) return;
            const sequence = ++copySequence.current;
            const revision = copyVersion.current.revision;
            const isCurrent = () =>
              sequence === copySequence.current &&
              revision === copyVersion.current.revision;
            setGenerationError(null);
            try {
              const source = await loadAgent(sourceAgentId);
              if (!isCurrent()) return;
              copyCallbacks.current.applyPrompt(source.systemPrompt);
              if (copyCallbacks.current.escalationAllowed) copyCallbacks.current.onCopyBlock?.(
                source.escalationPrompt ?? null,
              );
            } catch (error) {
              if (!isCurrent()) return;
              setGenerationError(
                error instanceof Error
                  ? error.message
                  : "Агент-источник недоступен",
              );
            }
          }}
          disabled={disabled || !sourceAgentId || !loadAgent}
        >
          Скопировать промпт
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => chooseMode(null)}
          disabled={disabled}
        >
          Выбрать другой способ
        </Button>
      </div>
    </div>
  );
}
